import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { NgxMaskDirective } from 'ngx-mask';
import { BehaviorSubject, Observable, Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { Masks } from '@app/constants';
import { Patterns } from '@app/constants/patterns.constant';
import { Filter, FilterConfig, TableFilter } from '@app/interfaces';
import { BaseServerFilterEntity } from '@app/models';
import { PopoverPosition, PopoverService } from '@app/shared/components/popover/popover.service';
import { FormComponent, FormMode } from '@app/shared/forms/components/form.component';
import { AccountStructureService } from '@app/shared/services';
import { isNil } from '@app/utils';
import { NgForKeyDirective } from '../../../directives/ng-for-track-by-id.directive';

@Component({
	selector: 'sl-filters',
	templateUrl: './filters.component.html',
	styleUrls: ['./filters.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		NgIf,
		MatFormFieldModule,
		NgClass,
		MatInputModule,
		NgxMaskDirective,
		MatIconModule,
		MatButtonModule,
		MatChipsModule,
		NgFor,
		NgForKeyDirective,
		FormComponent,
		AsyncPipe,
	],
})
export class FiltersComponent implements AfterViewInit, OnDestroy, OnInit {
	@ViewChild('filtersForm')
	formTemplate: TemplateRef<any>;

	@ViewChild('searchInput')
	searchInput: ElementRef;

	@ViewChild('searchAccountInput')
	searchAccountInput: ElementRef;

	@Input()
	searchOnly = false;

	@Input()
	hideSearch = false;

	@Input()
	disableFilters = false;

	@Input()
	showChipKey = false;

	@Input()
	fields: FormlyFieldConfig[] = [];

	@Input()
	filters: TableFilter<any> = {
		search: '',
	};

	@Input()
	isAccountSearch: Observable<boolean> = new BehaviorSubject(false);

	@Input()
	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: false,
			selects: {},
		},
	};

	@Input()
	config: FilterConfig<any>[] = [];

	@Input()
	removeBottomBorder = false;

	/**
	 * If true, filter chips configured with 'All' related variables will always be visible to the user.
	 */
	@Input()
	alwaysShowConfiguredAllChips = false;

	/**
	 * Set to true to clear the search input value. Will automatically reset to false after clearing.
	 */
	@Input()
	set clearSearch(value: boolean) {
		if (value) {
			this.clearSearchValue();
		}
	}

	@Output()
	filtersUpdated = new EventEmitter<any>();
	public search$ = new BehaviorSubject('');

	private configMap: { [key: string]: FilterConfig<any> } = {};

	customPatterns = {
		0: { pattern: new RegExp(Patterns.numericAccount) },
		X: { pattern: new RegExp(Patterns.alphaNumericAccount) },
	};

	private readonly destroy$ = new Subject();
	accountStructure: any;

	constructor(
		private readonly popoverService: PopoverService,
		private readonly accountStructureService: AccountStructureService,
	) {}

	ngOnInit(): void {
		this.config.forEach((config) => {
			this.configMap[String(config.key)] = config;
		});
	}

	ngAfterViewInit(): void {
		this.initialize(this.filters?.search);
		// TODO: IsAccountSearch and AccountStructureService needs to be removed from FilterComponent.
		this.isAccountSearch.pipe(takeUntil(this.destroy$)).subscribe((isAccountSearch: boolean) => {
			if (isAccountSearch && this.accountStructureService) {
				this.accountStructureService.getStructure().subscribe((data) => {
					if (data.accountStructure.UnifiedAccountMask.length === 42) {
						this.accountStructure = Masks.COAAccount;
					} else {
						this.accountStructure = data.accountStructure.UnifiedAccountMask;
					}
				});
			} else {
				this.accountStructure = null;
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	initialize(initialSearch = ''): void {
		if (!this.hideSearch) {
			this.searchInput.nativeElement.value = initialSearch;
			fromEvent<any>(this.searchInput.nativeElement, 'keyup')
				.pipe(
					map((event) => event.target.value),
					startWith(initialSearch),
					debounceTime(150),
					distinctUntilChanged(),
				)
				.subscribe(this.search$);
			this.search$.pipe(takeUntil(this.destroy$)).subscribe((search: string) => {
				this.applySearch(search);
			});
			fromEvent<any>(this.searchAccountInput.nativeElement, 'keyup')
				.pipe(
					map((event) => event.target.value),
					startWith(initialSearch),
					debounceTime(150),
					distinctUntilChanged(),
				)
				.subscribe(this.search$);
		}
	}

	renderChip(chip: { key: string; value: any }): string {
		let value = chip.value;

		const config = this.configMap[chip.key];
		if (config && typeof config.transform === 'function') {
			value = config.transform(chip);
		}

		return value;
	}

	generateChipDisplay(filter: { key: string; value: any }): string {
		const config = this.configMap[filter.key];
		const keyDisplay = !isNil(config?.label) && config.label.length > 0 ? config.label : filter.key.replace(/([A-Z])/g, ' $1');
		const finalKeyDisplay = keyDisplay.toUpperCase();

		if (this.alwaysShowConfiguredAllChips) {
			if (isNil(filter.value) && !isNil(config?.allSelectValue)) {
				return `${finalKeyDisplay} : ${config?.allSelectLabel ?? config?.allSelectValue}`;
			}
			if (isNil(filter.value) && !isNil(config?.allEffectiveValue)) {
				return `${finalKeyDisplay} : ${config?.allEffectiveValue}`;
			}
		}

		return `${finalKeyDisplay} : ${filter.value}`;
	}

	onSubmit(model: TableFilter<any>): void {
		const data: any = {};
		Object.entries(model).forEach(([key, value]) => {
			const keyConfig = this.configMap[key];
			data[key] = keyConfig?.allSelectValue === value ? keyConfig?.allEffectiveValue : value;
		});
		this.filtersUpdated.emit(data);
		this.popoverService.close();
	}

	applySearch(value: string): void {
		this.filtersUpdated.emit({
			...this.filters,
			search: value,
		});
	}

	removeFilter(key: string, emitUpdated = true): void {
		let filterValue: any = null;

		const currentFilterValue = this.filters[key];
		if (currentFilterValue !== null) {
			const config = this.configMap[key];

			if (key === 'search') {
				filterValue = '';
			} else if (!isNil(config?.clearedValue)) {
				filterValue = config.clearedValue;
			} else if (Array.isArray(currentFilterValue)) {
				filterValue = [];
			} else if (currentFilterValue.constructor === Object) {
				filterValue = {};
				Object.keys(currentFilterValue).forEach((i) => (filterValue[i] = null));
			}
		}

		this.filters = { ...this.filters, [key]: filterValue };
		if (emitUpdated) {
			this.filtersUpdated.emit({
				...this.filters,
			});
		}
	}

	clearFilters(): void {
		Object.keys(this.filters)
			.filter((key) => key !== 'search')
			.forEach((key) => this.removeFilter(key, false));

		this.filtersUpdated.emit({
			...this.filters,
		});
	}

	closePopover(): void {
		this.popoverService.close();
	}

	openFilters(event: Event): void {
		const options = {
			data: {
				fields: this.fields,
				options: { ...this.options, formState: { ...this.options?.formState, mode: FormMode.Edit } },
				filters: cloneDeep(this.filters),
				form: new UntypedFormGroup({}),
			},
			position: 'bottom' as PopoverPosition,
			target: event.target as HTMLElement,
		};
		this.popoverService.open(this.formTemplate, options);
	}

	getAppliedFilters(): Filter[] {
		const appliedFilters = Object.entries(this.filters)
			.map(([key, value]) => {
				const filter: Filter = { key, value };
				const config = this.configMap[key];

				if (BaseServerFilterEntity.isBaseServerFilterEntity(filter.value)) {
					filter.value = filter.value.Label;
				}

				if (config && typeof config.transform === 'function') {
					filter.value = config.transform({ key, value });
				}
				return filter;
			})
			.filter((filter: Filter) => {
				if (filter.key === 'search') {
					return false;
				}

				if (this.alwaysShowConfiguredAllChips) {
					if (isNil(filter.value) && !isNil(this.configMap[filter.key]?.allSelectValue)) {
						return true;
					}
					if (isNil(filter.value) && !isNil(this.configMap[filter.key]?.allEffectiveValue)) {
						return true;
					}
				}

				if (Array.isArray(filter.value) && !filter.value.length) {
					return false;
				}

				return filter.value !== null && filter.value !== undefined && filter.value !== '';
			});

		// sort by ordinal
		appliedFilters.sort((a: Filter, b: Filter) => {
			const ordinalA = this.configMap[a.key]?.ordinal || 0;
			const ordinalB = this.configMap[b.key]?.ordinal || 0;
			return ordinalA - ordinalB;
		});

		return appliedFilters;
	}

	hasFilters(): boolean {
		const appliedFilters = this.getAppliedFilters();
		return appliedFilters.length > 0;
	}

	private clearSearchValue(): void {
		// Clear both search inputs if they exist
		if (this.searchInput?.nativeElement) {
			this.searchInput.nativeElement.value = '';
		}
		if (this.searchAccountInput?.nativeElement) {
			this.searchAccountInput.nativeElement.value = '';
		}

		// Update the search$ observable and apply the search
		this.search$.next('');
		this.applySearch('');
	}
}
